import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { Menu, X } from 'lucide-react';

export interface SidebarNavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  /** Hide the row when this returns false. Defaults to always visible. */
  visible?: boolean;
}

export interface SidebarGroup {
  /** Eyebrow label rendered above the items (empty string suppresses). */
  label: string;
  items: SidebarNavItem[];
}

export interface SidebarBrand {
  /** Small icon shown in the brand block (e.g. <Sparkles />). */
  mark: ReactNode;
  title: string;
  subtitle?: string;
}

export interface SidebarProps {
  brand?: SidebarBrand;
  groups: SidebarGroup[];
  footer?: ReactNode;
  /** Width override; defaults to 244px to match apps/web. */
  width?: number;
}

function cn(...parts: Array<string | false | undefined | null>) {
  return parts.filter(Boolean).join(' ');
}

/**
 * Structural sidebar primitive. Apps own the nav config — pass in `groups`,
 * the brand block and a footer slot. The component handles the dark surface,
 * mobile open/close, and active-state styling.
 */
export function Sidebar({ brand, groups, footer, width = 244 }: SidebarProps) {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (href: string) =>
    href === '/' || href.endsWith('/dashboard') || href.endsWith('/admin')
      ? location.pathname === href
      : location.pathname.startsWith(href);

  const renderGroup = (group: SidebarGroup, gi: number) => {
    const visible = group.items.filter((i) => i.visible !== false);
    if (visible.length === 0) return null;
    return (
      <div key={`${group.label}-${gi}`} className="mt-6 first:mt-4">
        {group.label ? (
          <div
            className="px-3 mb-2 text-[10.5px] font-semibold uppercase text-white/45"
            style={{ letterSpacing: '0.12em', fontFamily: 'var(--font-mono)' }}
          >
            {group.label}
          </div>
        ) : null}
        <div className="flex flex-col gap-0.5">
          {visible.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setMobileOpen(false)}
                aria-current={active ? 'page' : undefined}
                className={cn('ed-nav-link', active && 'active')}
              >
                <Icon className="h-[15px] w-[15px] shrink-0" strokeWidth={1.75} />
                <span className="flex-1 truncate">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    );
  };

  const inner = (
    <nav className="flex h-full flex-col px-3 pb-3">
      {brand ? (
        <>
          <div className="flex items-center gap-3 px-3 py-5 text-white">
            <span className="shrink-0">{brand.mark}</span>
            <div className="flex flex-col leading-tight min-w-0">
              <span
                className="text-white truncate"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 500,
                  fontSize: '17px',
                  letterSpacing: '-0.035em',
                  lineHeight: 1.1,
                }}
              >
                {brand.title}
              </span>
              {brand.subtitle ? (
                <span
                  className="text-white/55 truncate"
                  style={{
                    fontSize: '10.5px',
                    fontFamily: 'var(--font-mono)',
                    letterSpacing: '0.04em',
                    marginTop: '4px',
                  }}
                >
                  {brand.subtitle}
                </span>
              ) : null}
            </div>
          </div>

          <div
            className="-mx-3 h-px"
            style={{ background: 'var(--sidebar-border)' }}
          />
        </>
      ) : null}

      <div className="flex-1 overflow-y-auto">{groups.map(renderGroup)}</div>

      {footer ? (
        <div
          className="-mx-3 mt-2 border-t pt-3 px-5 text-[10px]"
          style={{ borderColor: 'var(--sidebar-border)', color: 'var(--sidebar-fg)' }}
        >
          {footer}
        </div>
      ) : null}
    </nav>
  );

  return (
    <>
      <button
        className="fixed left-4 top-4 z-50 rounded-md bg-[var(--sidebar-bg)] text-white p-2 lg:hidden"
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label="Toggle menu"
      >
        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 transition-transform lg:static lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
        style={{ background: 'var(--sidebar-bg)', width }}
      >
        {inner}
      </aside>
    </>
  );
}
